import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Box,
    Typography,
    Autocomplete,
} from "@mui/material";
import styled from "@emotion/styled";
import { useDispatch } from "react-redux";
import { createQuote } from "../redux/slices/quote";
import { showAlert } from "../redux/slices/alerts";
import { getAccounts } from "../redux/ducks/account";
import { useHasPermission } from "../utils/permissionUtils";
import MarkdownEditor from "./MarkdownEditor";
import FilePondUploader from "./FilePondUploader";
import { restoreAttachments } from "../utils/uploadUtils";
import QuoteTemplatePicker from "./templates/QuoteTemplatePicker";
import { PROJECT_TYPE_OPTIONS } from "../utils/projectTypes";

const DialogContentStyled = styled(DialogContent)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    padding: theme.spacing(3),
}));

const SubmitSpinner = styled(CircularProgress)({
    marginLeft: 8,
});

const QuoteRequestDialog = ({ open, onClose, accountId, onCreated }) => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const isManagerMode = hasPermission("canManageQuotes");
    const showAccountPicker = !accountId;

    const [quoteName, setQuoteName] = useState("");
    const [description, setDescription] = useState("");
    const [projectType, setProjectType] = useState("");
    const [quoteTemplateId, setQuoteTemplateId] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editorKey, setEditorKey] = useState(0);

    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [accountsLoading, setAccountsLoading] = useState(false);

    useEffect(() => {
        if (!open) {
            setQuoteName("");
            setDescription("");
            setProjectType("");
            setQuoteTemplateId("");
            setAttachments([]);
            setIsUploading(false);
            setIsSubmitting(false);
            setSelectedAccount(null);
            setEditorKey((k) => k + 1);
        }
    }, [open]);

    useEffect(() => {
        if (!open || !showAccountPicker) return;
        setAccountsLoading(true);
        dispatch(getAccounts(0, 0, null, false, (data) => {
            const list = Array.isArray(data) ? data : [];
            setAccounts(list);
            setAccountsLoading(false);
            if (list.length === 1) {
                setSelectedAccount(list[0]);
            }
        }));
    }, [open, showAccountPicker, dispatch]);

    const handleClose = () => {
        if (!isSubmitting) onClose();
    };

    const targetAccountId = showAccountPicker ? selectedAccount?._id : accountId;

    const handleSubmit = () => {
        setIsSubmitting(true);
        try {
            dispatch(
                createQuote({
                    data: {
                        account: targetAccountId,
                        name: quoteName.trim(),
                        description,
                        projectType,
                        ...(isManagerMode && quoteTemplateId ? { quoteTemplateId } : {}),
                        attachments: restoreAttachments(attachments),
                    },
                    callback: (response) => {
                        setIsSubmitting(false);
                        dispatch(showAlert({
                            severity: "success",
                            message: isManagerMode
                                ? (quoteTemplateId
                                    ? "Quote created from the template and ready to review in Draft."
                                    : "Quote draft created. The system is generating an initial estimate.")
                                : "Quote request submitted. A manager will review and send you a quote.",
                        }));
                        onCreated?.(response);
                        onClose();
                    },
                })
            );
        } catch (error) {
            setIsSubmitting(false);
            dispatch(showAlert({ severity: "error", message: error.message || "Failed to submit quote request" }));
        }
    };

    const isSubmitDisabled =
        !targetAccountId ||
        quoteName.trim() === "" ||
        !description ||
        projectType === "" ||
        isUploading ||
        isSubmitting;

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>{isManagerMode ? "New Quote" : "Request a Quote"}</DialogTitle>
            <DialogContentStyled>
                {showAccountPicker && (
                    <Autocomplete
                        options={accounts}
                        loading={accountsLoading}
                        getOptionLabel={(o) => o.name || ""}
                        value={selectedAccount}
                        onChange={(_, v) => setSelectedAccount(v)}
                        isOptionEqualToValue={(o, v) => o._id === v._id}
                        renderInput={(params) => (
                            <TextField {...params} label="Account" required placeholder="Select the account this quote is for" />
                        )}
                        disabled={isSubmitting}
                        fullWidth
                    />
                )}

                <TextField
                    fullWidth
                    required
                    label="Project Name"
                    value={quoteName}
                    onChange={(e) => setQuoteName(e.target.value)}
                    disabled={isSubmitting}
                    placeholder="e.g. Client Portal Redesign"
                />

                <Box>
                    <Typography variant="caption" color="text.secondary">
                        {isManagerMode ? "Describe the work the client requested" : "Describe the work"}
                    </Typography>
                    <MarkdownEditor
                        key={editorKey}
                        initialContent={null}
                        onChange={(content) => setDescription(content)}
                    />
                </Box>

                <FormControl fullWidth>
                    <InputLabel>Project Type</InputLabel>
                    <Select
                        value={projectType}
                        label="Project Type"
                        onChange={(e) => setProjectType(e.target.value)}
                        disabled={isSubmitting}
                    >
                        {PROJECT_TYPE_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {isManagerMode && (
                    <QuoteTemplatePicker
                        value={quoteTemplateId}
                        onChange={(id) => setQuoteTemplateId(id)}
                        disabled={isSubmitting}
                        label="Quote template (optional)"
                        helperText="Pick one of your standard engagements and the quote is built from it straight away, ready to review. Leave empty to let the AI draft it. Clients never see templates."
                    />
                )}

                <Box>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                        {isManagerMode
                            ? "Attach the client's request (PDF, email export, screenshot, etc.) — optional"
                            : "Attach a quote-request PDF or any reference files — optional"}
                    </Typography>
                    <FilePondUploader
                        setAttachments={setAttachments}
                        onUploadStatusChange={setIsUploading}
                    />
                </Box>
            </DialogContentStyled>
            <DialogActions>
                <Button onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={isSubmitDisabled}>
                    {isSubmitting ? (
                        <>Submitting... <SubmitSpinner size={16} /></>
                    ) : (
                        isManagerMode ? "Create Quote" : "Submit Request"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default QuoteRequestDialog;
